import { MAX_RECENT_COMPANIES } from '@/lib/appConstants.js';
import toSafeUserStateString from '@/redux/user/toSafeUserStateString.js';

const toCompanyItem = (company) => {
  const normalizedName = toSafeUserStateString(company?.name);
  const isPersonal = company?.isPersonal === true;

  return {
    id: toSafeUserStateString(company?.id),
    label: isPersonal ? normalizedName || 'Personal account' : normalizedName || 'Untitled organization',
    isPersonal
  };
};

/**
 * Builds the organization switcher list: the user's own organizations first,
 * then recently visited organizations that are not already owned, appended to
 * the bottom of the same list (no separate section) and capped at
 * MAX_RECENT_COMPANIES.
 *
 * Owned organizations are ordered by visit recency (recentCompanies is
 * most-recent-first), with never-visited ones keeping the API's alphabetical
 * order after them.
 */
const buildCompanyItems = ({ companies = [], recentCompanies = [] } = {}) => {
  const safeRecents = Array.isArray(recentCompanies) ? recentCompanies : [];
  const recencyRankById = new Map();

  for (const [index, company] of safeRecents.entries()) {
    const id = toSafeUserStateString(company?.id);

    if (id && !recencyRankById.has(id)) {
      recencyRankById.set(id, index);
    }
  }

  const ownedItems = (Array.isArray(companies) ? companies : []).map(toCompanyItem).sort((a, b) => (recencyRankById.get(a.id) ?? Number.POSITIVE_INFINITY) - (recencyRankById.get(b.id) ?? Number.POSITIVE_INFINITY));
  const seenIds = new Set(ownedItems.map((item) => item.id).filter(Boolean));
  const recentItems = [];

  for (const company of safeRecents) {
    const item = toCompanyItem(company);

    if (!item.id || seenIds.has(item.id)) {
      continue;
    }

    seenIds.add(item.id);
    recentItems.push(item);

    if (recentItems.length >= MAX_RECENT_COMPANIES) {
      break;
    }
  }

  return [...ownedItems, ...recentItems];
};

export default buildCompanyItems;
